import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiFetch, ApiClientError } from './client';
import { battleKeys, type BattleViewDto, type KeeperDto } from './battles';
import type { TierBandDto } from './minigames';

// Server: arena/ArenaDtos.java's ArenaPartyPreviewDto — a no-free-text party preview (C3:
// opponents are shown by species/level/element only, never a message) shared between a profile's
// own duelParty and an opponent's partyPreview. `element` is the Element enum's name (e.g.
// "EMBER") — same wire convention battles.ts's own ActorDto.element already uses — read straight
// off the frozen defense_json snapshot server-side, never re-derived.
export type ArenaPartyPreviewDto = { speciesCode: string; level: number; element: string };

// Server: arena/ArenaSeasonDtos.java's SeasonDto — the currently running season, as the profile/
// arena screen shows it. `endsAt` is the window's exclusive upper bound (C3: rendered as an
// anticipation clock, never inside a button).
export type SeasonDto = { code: string; name: string; endsAt: string };

// Server: arena/ArenaSeasonDtos.java's LastSeasonDto — what an account placed in the most
// recently CLOSED season it entered. `rankInBand` is 1-based WITHIN the band — R5's rule is that
// a global rank never reaches the wire, so this is the only placement figure a client ever sees.
// `rewardItemCodes` are the band's own reward list from the season content doc, echoed back so
// the card can show what was granted without the client re-deriving it from content it doesn't
// have.
export type LastSeasonDto = {
  code: string; name: string; band: string; rankInBand: number; rewardItemCodes: string[];
};

// Server: arena/ArenaDtos.java's ArenaProfileDto — GET /arena/profile, and the return of
// PUT /arena/party. `partySetAt`/`duelParty` are both null together (no loadout ever saved) or
// both non-null.
//
// The wire rename (Task 9, design spec §4.3): `defenseSetAt`/`defenseParty` became
// `partySetAt`/`duelParty`, because since P0 the saved loadout is the party a keeper DUELS with,
// not a snapshot that defends while they are away.
//
// attacksToday/rewardedToday are already rollover-adjusted for "today" (UTC) even if the
// underlying row hasn't been physically reset yet (ArenaService.today).
//
// The season fields: `season` is the currently running season, or null outside every authored
// window. `band`/`rankInBand` are this account's LIVE ladder placement — null/0 together when it
// is not an entrant (P3: a saved party AND at least one rated duel since the last closure). They
// are deliberately NOT gated on `season` being non-null — a board that vanished between seasons
// would tell a keeper their placing was erased when it was not. A GLOBAL rank never appears here
// (R5) — only the rank within the band. `lastSeason` is what they placed in the most recently
// CLOSED season they entered.
export type ArenaProfileDto = {
  rating: number;
  wins: number;
  losses: number;
  partySetAt: string | null;
  duelParty: ArenaPartyPreviewDto[] | null;
  attacksToday: number;
  rewardedToday: number;
  seasonWins: number;
  seasonLosses: number;
  season: SeasonDto | null;
  band: string | null;
  rankInBand: number;
  lastSeason: LastSeasonDto | null;
  // House keepers (R3): the rated queue opens once `keepersNeeded` distinct keepers have joined a
  // battle since this ISO week's Monday 00:00 UTC. Optional because an older server omits them —
  // absent reads as "open", i.e. the screen as it was.
  ratedOpen?: boolean;
  keepersThisWeek?: number;
  keepersNeeded?: number;
};

// Server: arena/ArenaDtos.java's HouseKeeperDto — one row of GET /arena/house, ordered by
// `minLevel`. `accountId` is the target for the ordinary POST /arena/practice; the server does not
// enforce `minLevel`, the screen does.
export type HouseKeeperDto = {
  accountId: string;
  name: string;
  blurb: string;
  minLevel: number;
  partyPreview: ArenaPartyPreviewDto[];
};

// Server: arena/ArenaDtos.java's ArenaOpponentDto — one row of GET /arena/opponents (the PRACTICE
// list since Task 6/§3.7 — max 3, per ArenaService.opponents' matchmaking window).
export type ArenaOpponentDto = {
  accountId: string;
  displayName: string;
  rating: number;
  partyPreview: ArenaPartyPreviewDto[];
};

// Server: arena/ArenaDtos.java's ArenaHistoryEntryDto — one row of GET /arena/history (last 20,
// both seats folded into one list, newest first). `role` is challenger|opponent (Task 9, §3.6 —
// it was attacker|defender while the rated mode was a snapshot attack; a live duel has no
// attacker). `outcome`/`ratingDelta` are already normalised to THIS account's own perspective.
//
// `reportable` is always true on a row that appears here at all; it is on the wire as a real
// field rather than assumed by the client so a future non-replayable row kind needs no client
// change. `rematchable` is P6's affordance — since the owner ruling of 2026-09-13
// (`docs/DECISIONS.md` #3) it is "not blocked either way, and the opponent still has a saved duel
// party"; the friendship conjunct it used to carry went with the friends-only rule.
export type ArenaHistoryEntryDto = {
  battleId: number;
  role: 'challenger' | 'opponent';
  opponentAccountId: string;
  opponentName: string;
  outcome: string;
  ratingDelta: number;
  ts: string;
  reportable: boolean;
  rematchable: boolean;
};

// Server: arena/ArenaSeasonDtos.java's ArenaLadderRowDto — one ladder row. `rank` is a global
// rank and is the ONE place R5 permits it on the wire: used for medals and for positioning a row
// within its own band, never to render a "you are #N of M" figure.
export type ArenaLadderRowDto = { displayName: string; rating: number; rank: number };

// Server: arena/ArenaSeasonDtos.java's ArenaLadderDto — GET /arena/leaderboard. `yourBand`/
// `yourRankInBand`/`yourRating` are null/0 together when the viewer is not an entrant this season
// — the top three are still served so the screen is never empty. `aroundYou` is up to 11 rows
// centred on the viewer, clamped to the board's edges. `yourBand` is the server's `TierBand`
// record — same shape minigames.ts's own TierBandDto already gives GET /minigames/*/leaderboard,
// reused here rather than re-declared.
export type ArenaLadderDto = {
  totalEntrants: number;
  yourBand: TierBandDto | null;
  yourRankInBand: number;
  yourRating: number | null;
  top: ArenaLadderRowDto[];
  aroundYou: ArenaLadderRowDto[];
};

// Server: arena/ArenaSeasonDtos.java's ArenaReportDto — GET /arena/reports/{battleId} (P5), a
// finished duel replayable by EITHER keeper. Only a resolved, rated duel is ever served.
// `viewerSide` is echoed verbatim from BattleViewDto: "party" for the challenger (seated from
// index 0), "enemies" for the opponent — a client mirrors the fight when it reads "enemies".
// `outcomeForViewer`/`ratingDeltaForViewer` are the CALLER's own seat, never the raw
// challenger-perspective fields.
export type ArenaReportDto = {
  battle: BattleViewDto;
  challengerName: string;
  opponentName: string;
  viewerSide: 'party' | 'enemies';
  outcomeForViewer: string;
  ratingDeltaForViewer: number;
};

// Server: arena/QueueStateDto.java — the duel queue as one keeper sees it (design spec §3.2,
// P10). `waiting`: battleId is null, waitedSeconds counts from the ENQUEUE (not the last
// heartbeat) — the matchmaking band widens past 20 seconds of this number. `matched`: battleId is
// the battle to open — reported exactly once (the poll that reports it also consumes it
// server-side). `idle`: neither queued nor holding an unread match; waitedSeconds is 0.
export type QueueStateDto = {
  state: 'waiting' | 'matched' | 'idle';
  battleId: number | null;
  waitedSeconds: number;
};

// Server: battle/KeeperDto.java, re-exported under arena's own vocabulary — a challenge names two
// keepers (challenger/challenged) by exactly this shape (accountId + display handle, no
// companion).
export type { KeeperDto };

// Server: arena/DuelChallengeDtos.java's ChallengeDto — one two-seat duel-invite room
// (`battle_lobbies(mode='duel')`, design spec §3.3). `challenged` is null only in the brief
// instant between `accept`'s promote and its delete, per DuelChallengeService.otherSeat's own
// javadoc — every challenge a client ever reads off `/challenges/mine` has a real invitee.
export type ChallengeDto = {
  id: number;
  challenger: KeeperDto;
  challenged: KeeperDto | null;
  expiresAt: string;
};

// Server: arena/DuelChallengeDtos.java's ChallengeInboxDto — GET /arena/challenges/mine. `outgoing`
// is the (at most one) challenge this keeper has sent; `incoming` is every challenge sent to them,
// newest first.
export type ChallengesMineDto = { outgoing: ChallengeDto | null; incoming: ChallengeDto[] };

// Server: arena/DuelChallengeDtos.java's ChallengeAcceptedDto — the return of
// POST /arena/challenges/{id}/accept: only the id of the duel just started (the accepter's own
// battle screen re-fetches the full BattleViewDto by that id, same as every other "a mutation
// started a battle, go open it" flow in this codebase).
export type ChallengeAcceptedDto = { battleId: number };

export const arenaKeys = {
  all: ['arena'] as const,
  profile: ['arena', 'profile'] as const,
  opponents: ['arena', 'opponents'] as const,
  history: ['arena', 'history'] as const,
  house: ['arena', 'house'] as const,
  ladder: ['arena', 'ladder'] as const,
  report: (battleId: number) => ['arena', 'report', battleId] as const,
  queue: ['arena', 'queue'] as const,
  challenges: ['arena', 'challenges'] as const,
};

export const getArenaProfile = () => apiFetch<ArenaProfileDto>('/arena/profile');

/** `options.refetchInterval` is the same passthrough `useQueue`/`useChallenges` use — the arena
 *  screen decides the cadence (see ArenaScreen's `RESULT_POLL_MS`). */
export function useArenaProfile(options?: { refetchInterval?: number | false }) {
  return useQuery({ queryKey: arenaKeys.profile, queryFn: getArenaProfile, refetchInterval: options?.refetchInterval });
}

export const getArenaOpponents = () => apiFetch<ArenaOpponentDto[]>('/arena/opponents');

// 404 not_available (ArenaNotAvailableException) when the arena_pvp flag is off for this account
// (R7's kill switch) — profile/party/history stay reachable per that exception's own javadoc
// ("purely an attacking-is-paused lever, not a data-access one"), so only opponents/practice/
// ladder/queue/challenges need to handle it. Mapped to `[]` here (an unreachable arena reads the
// same as "no opponents right now" from this list's own consumer's point of view) rather than
// surfaced as a distinct not-available state — screens that need to distinguish "flag off" from
// "no opponents" should check useFlags() directly instead (flags.ts).
export function useArenaOpponents() {
  return useQuery({
    queryKey: arenaKeys.opponents,
    queryFn: async (): Promise<ArenaOpponentDto[]> => {
      try {
        return await getArenaOpponents();
      } catch (err) {
        if (err instanceof ApiClientError && err.code === 'not_available') return [];
        throw err;
      }
    },
  });
}

export const getHouseKeepers = () => apiFetch<HouseKeeperDto[]>('/arena/house');

/** GET /arena/house. `not_available` (flag off) reads as no keepers, like useArenaOpponents. */
export function useHouseKeepers() {
  return useQuery({
    queryKey: arenaKeys.house,
    queryFn: async (): Promise<HouseKeeperDto[]> => {
      try {
        return await getHouseKeepers();
      } catch (err) {
        if (err instanceof ApiClientError && err.code === 'not_available') return [];
        throw err;
      }
    },
  });
}

export const getArenaHistory = () => apiFetch<ArenaHistoryEntryDto[]>('/arena/history');

export function useArenaHistory(options?: { refetchInterval?: number | false }) {
  return useQuery({ queryKey: arenaKeys.history, queryFn: getArenaHistory, refetchInterval: options?.refetchInterval });
}

export const getArenaLadder = () => apiFetch<ArenaLadderDto>('/arena/leaderboard');

/**
 * GET /arena/leaderboard. Maps `not_available` (arena_pvp flag off) to `null`, the same
 * "unreachable arena reads as an absent board" convention useArenaOpponents follows for its own
 * `[]` — a screen that needs to distinguish "flag off" from "no ladder yet" should check
 * useFlags() directly. `enabled` (default true) lets a caller defer the fetch — e.g. a ladder
 * sheet that only mounts once opened.
 */
export function useArenaLadder(enabled = true) {
  return useQuery({
    queryKey: arenaKeys.ladder,
    enabled,
    queryFn: async (): Promise<ArenaLadderDto | null> => {
      try {
        return await getArenaLadder();
      } catch (err) {
        if (err instanceof ApiClientError && err.code === 'not_available') return null;
        throw err;
      }
    },
  });
}

export const getArenaReport = (battleId: number) => apiFetch<ArenaReportDto>(`/arena/reports/${battleId}`);

/** GET /arena/reports/{battleId} (P5) — a finished duel, replayable by either keeper. 404 for a
 *  stranger, a still-running duel, or a practice fight (opaque `not_found`, same as every other
 *  "not visible to you" refusal in this codebase). Disabled while `battleId` is undefined, same
 *  `?? -1` placeholder-key convention useBattle already establishes. */
export function useArenaReport(battleId: number | undefined) {
  return useQuery({
    queryKey: arenaKeys.report(battleId ?? -1),
    queryFn: () => getArenaReport(battleId as number),
    enabled: battleId != null,
  });
}

export const getQueue = () => apiFetch<QueueStateDto>('/arena/queue');

/**
 * GET /arena/queue (§3.2) — the client's presence poll, which ALSO refreshes this keeper's own
 * heartbeat server-side as a side effect of merely reading it. `options.refetchInterval` is the
 * same passthrough useBattle/useAuction/useLobby already establish — the screen decides the 2 s
 * cadence design spec §3.2 calls for (and turns polling off once the state stops being
 * `waiting`), this hook just wires whatever interval it's given straight into the query.
 */
export function useQueue(options?: { refetchInterval?: number | false }) {
  return useQuery({
    queryKey: arenaKeys.queue,
    queryFn: getQueue,
    refetchInterval: options?.refetchInterval,
  });
}

export const enqueue = () => apiFetch<QueueStateDto>('/arena/queue', { method: 'POST' });

/** POST /arena/queue — joins the duel queue (or refreshes a membership that already exists; see
 *  DuelQueueService.enqueue's own javadoc — re-enqueueing does not restart the wait). Refusals
 *  (`not_available`, `arena_cap`, `no_party` — the design spec's own §4.3 code for a keeper with
 *  no saved duel party — and `battle_active`) are left to throw; a screen reads them off
 *  `mutation.error` the same way ShopScreen.tsx already does for `not_found`. Invalidates
 *  arenaKeys.queue so the next poll (or an immediate refetch) picks up the fresh `waiting` state. */
export function useEnqueue() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: enqueue,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: arenaKeys.queue }),
  });
}

export const leaveQueue = () => apiFetch<null>('/arena/queue', { method: 'DELETE' });

/** DELETE /arena/queue — leaves the queue (a no-op for a keeper who was never in it). Invalidates
 *  arenaKeys.queue so the queue sheet's own poll immediately reads `idle` rather than waiting for
 *  its next 2 s tick. */
export function useLeaveQueue() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: leaveQueue,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: arenaKeys.queue }),
  });
}

export const getChallenges = () => apiFetch<ChallengesMineDto>('/arena/challenges/mine');

/**
 * GET /arena/challenges/mine. `options.refetchInterval` is the same passthrough `useQueue`/
 * `useLobby` already establish — the caller decides the cadence (the arena's challenge banner
 * polls at 2 s, but ONLY while a challenge is actually open; a screen with no challenge in flight
 * has nothing to poll for) rather than this hook baking one in for every consumer.
 *
 * Maps `not_available` (the `arena_pvp` flag off for this account) to an EMPTY inbox, the same
 * "unreachable arena reads as an absent board" convention `useArenaOpponents`/`useArenaLadder`
 * already follow for their own `[]`/`null`. Without it (final whole-branch review, minor) a
 * switched-off arena left this query erroring and retrying — four failing requests on the
 * Notifications screen, which mounts the challenge inbox unconditionally — and a screen that
 * genuinely needs to tell "flag off" from "no challenges" should read useFlags() directly.
 */
export function useChallenges(options?: { refetchInterval?: number | false; enabled?: boolean }) {
  return useQuery({
    queryKey: arenaKeys.challenges,
    refetchInterval: options?.refetchInterval,
    enabled: options?.enabled,
    queryFn: async (): Promise<ChallengesMineDto> => {
      try {
        return await getChallenges();
      } catch (err) {
        if (err instanceof ApiClientError && err.code === 'not_available') {
          return { outgoing: null, incoming: [] };
        }
        throw err;
      }
    },
  });
}

/**
 * The two ways to name the keeper being challenged — DuelChallengeController's own
 * `CreateChallengeRequest(UUID accountId, String handle)`, which takes EXACTLY ONE of the pair and
 * answers 400 `validation` for both or neither. A union rather than one optional-pair object
 * precisely so "both" cannot be typed here at all: the caller either holds an id (a history row's
 * Rematch, a practice row) or only a name (the keeper picker's handle box).
 *
 * Owner ruling 2026-09-13 (`docs/DECISIONS.md` #3): a handle is an ADDRESS now, not a friend
 * lookup — the server resolves it case-insensitively against Decision #10's unique display names
 * and refuses an unknown one with the same opaque `not_found` a block gets.
 */
export type ChallengeTarget = { accountId: string } | { handle: string };

// POST /arena/challenges body {accountId} | {handle} — DuelChallengeController.CreateChallengeRequest.
// Serialised straight from the union, so exactly one key ever reaches the wire.
export const createChallenge = (target: ChallengeTarget) =>
  apiFetch<ChallengeDto>('/arena/challenges', { method: 'POST', body: JSON.stringify(target) });

/** Challenges ANY keeper to a rated duel (§3.3, as amended by the 2026-09-13 owner ruling —
 *  friendship is no longer the gate). Refusals (`not_available`, `not_found` opaque for
 *  unknown-handle/blocked/self/cannot-duel, `member_busy`, `arena_cap`, `no_party`,
 *  `arena_out_of_range`, `challenge_open`, `lobby_open`, `battle_active`, `rate_limited`) are left
 *  to throw. Invalidates arenaKeys.challenges — the caller's own `outgoing` slot is now filled. */
export function useChallenge() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: createChallenge,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: arenaKeys.challenges }),
  });
}

export const acceptChallenge = (id: number) =>
  apiFetch<ChallengeAcceptedDto>(`/arena/challenges/${id}/accept`, { method: 'POST' });

// Same typed-refusal contract as usePractice/useStartBattle for the one refusal a screen must act
// on rather than toast: `battle_active` (409, carries `activeBattleId` off
// `ApiClientError.details`) — the accepter's OWN active battle (DuelChallengeService.accept's own
// javadoc: the challenger's busy state is translated into `member_busy` instead, which is not
// this shape and is left to throw normally, same as `not_found`/`lobby_expired`/`rate_limited`).
export type AcceptChallengeResult =
  | { ok: true; battleId: number }
  | { ok: false; code: 'battle_active'; activeBattleId: number; message: string };

async function acceptChallengeTyped(id: number): Promise<AcceptChallengeResult> {
  try {
    const { battleId } = await acceptChallenge(id);
    return { ok: true, battleId };
  } catch (err) {
    if (err instanceof ApiClientError && err.code === 'battle_active') {
      const activeBattleId = Number(err.details?.activeBattleId ?? 0);
      return { ok: false, code: 'battle_active', activeBattleId, message: err.message };
    }
    throw err;
  }
}

/**
 * On success, invalidates battleKeys.byId(battleId) and battleKeys.active (GET /battles/active
 * now resolves to the just-started duel) — unlike usePractice/useStartBattle this cannot
 * setQueryData the full BattleViewDto directly, since ChallengeAcceptedDto carries only the id;
 * the battle screen it navigates to issues its own GET. Always invalidates arenaKeys.challenges —
 * the accepted room is gone either way.
 */
export function useAcceptChallenge() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: acceptChallengeTyped,
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: arenaKeys.challenges });
      if (!result.ok) return;
      queryClient.invalidateQueries({ queryKey: battleKeys.byId(result.battleId) });
      queryClient.invalidateQueries({ queryKey: battleKeys.active });
    },
  });
}

export const declineChallenge = (id: number) =>
  apiFetch<null>(`/arena/challenges/${id}/decline`, { method: 'POST' });

/** The invited keeper says no — 204/empty. Invalidates arenaKeys.challenges (the row is gone from
 *  `incoming`). */
export function useDeclineChallenge() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: declineChallenge,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: arenaKeys.challenges }),
  });
}

export const cancelChallenge = (id: number) =>
  apiFetch<null>(`/arena/challenges/${id}/cancel`, { method: 'POST' });

/** The challenger takes it back — 204/empty. Invalidates arenaKeys.challenges (the caller's own
 *  `outgoing` slot is free again). */
export function useCancelChallenge() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: cancelChallenge,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: arenaKeys.challenges }),
  });
}

// PUT /arena/party body {companionIds} — ArenaController.setParty (renamed from PUT /arena/defense
// at Task 9/10 — since P0 the saved loadout is the party a keeper DUELS with; the old path is kept
// server-side for one release as an alias, but this client stops sending it here). Refuses with
// validation (400, ArenaDefenseInvalidException — shape only, not 1..3 ids or a repeated id) if
// malformed; an unknown/foreign id is CompanionNotFoundException (not_found, 404) instead.
export const setParty = (companionIds: number[]) =>
  apiFetch<ArenaProfileDto>('/arena/party', {
    method: 'PUT',
    body: JSON.stringify({ companionIds }),
  });

/** NOT optimistic — the server owns whether the party is valid/owned. Invalidates arena profile
 *  (the new duelParty/partySetAt) directly via setQueryData (the PUT already returns the full
 *  fresh ArenaProfileDto, no round-trip refetch needed, same shape as shops.ts's usePurchase
 *  writing its response straight into walletKeys.all). Renamed from useSetDefense (Task 9/10). */
export function useSetParty() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (companionIds: number[]) => setParty(companionIds),
    onSuccess: (data) => queryClient.setQueryData(arenaKeys.profile, data),
  });
}

// POST /arena/practice body {defenderAccountId} — ArenaController.AttackRequest. The field name
// is deliberately unchanged from the pre-rename request body (ArenaController's own javadoc:
// "POST /arena/practice takes the same body" as the deprecated /arena/attacks alias) even though
// the design spec's own §4.3 sketch writes it as `{accountId}` — the Java record is the source of
// truth here. `accountId` in this function's own signature names the practice TARGET, whose saved
// loadout the AI drives. Returns the started BattleViewDto (kind="arena_practice") on success,
// unrated (§3.7/P9): no cap, no reward, no notification, no report.
export const practice = (accountId: string) =>
  apiFetch<BattleViewDto>('/arena/practice', {
    method: 'POST',
    body: JSON.stringify({ defenderAccountId: accountId }),
  });

// Typed result for battle_active (409, same BattleActiveBody shape ArenaController's own
// resolveActiveBody builds, ApiClientError.details.activeBattleId per client.ts's Task 14
// widening) so a screen can offer "Resume" instead of a dead-end toast — same contract
// battles.ts's useStartBattle follows for the identical refusal shape. Every other refusal
// (validation 400, not_available 404) still throws normally.
export type AttackResult =
  | { ok: true; battle: BattleViewDto }
  | { ok: false; code: 'battle_active'; activeBattleId: number; message: string };

async function practiceTyped(accountId: string): Promise<AttackResult> {
  try {
    const battle = await practice(accountId);
    return { ok: true, battle };
  } catch (err) {
    if (err instanceof ApiClientError && err.code === 'battle_active') {
      const activeBattleId = Number(err.details?.activeBattleId ?? 0);
      return { ok: false, code: 'battle_active', activeBattleId, message: err.message };
    }
    throw err;
  }
}

/**
 * Renamed from useAttack (Task 9/10 — the rated snapshot "attack" no longer exists; this is
 * always the unrated practice fight, §3.7/P9). On success, seeds battleKeys.byId(battle.id) with
 * the started battle (avoids a redundant GET the arena-battle screen would otherwise issue
 * immediately) and invalidates battleKeys.active plus arena opponents/history/profile — a
 * practice fight resolves via the SAME turn-submission flow every PvE battle uses
 * (battles.ts's useSubmitTurn), so nothing rating-related ever changes (there is no rating
 * involved), but the opponents list and history can still shift, and this mutation only ever
 * returns the freshly-STARTED (turn 0, not-yet-resolved) battle, matching BattleController's own
 * start()'s return shape.
 */
export function usePractice() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: practiceTyped,
    onSuccess: (result) => {
      if (!result.ok) return; // battle_active: nothing changed arena-side, nothing to invalidate
      queryClient.setQueryData(battleKeys.byId(result.battle.id), result.battle);
      queryClient.invalidateQueries({ queryKey: battleKeys.active });
      queryClient.invalidateQueries({ queryKey: arenaKeys.profile });
      queryClient.invalidateQueries({ queryKey: arenaKeys.opponents });
      queryClient.invalidateQueries({ queryKey: arenaKeys.history });
    },
  });
}
